import { NormalizedNumber } from '@sparkdotfi/common-universal'

import { darkenColor, formatPieChartValue, getPalletteColor } from '../../common/chart-utils'
import { LiquidityChartComponentProps } from '../types'
import { mapProtocolToLabel } from '../utils'
import { getArcs, getSeparators } from '@/ui/utils/chart-math'
import { cn } from '@/ui/utils/style'

export function LiquidityPieChart({
  items,
  highlightedIndex,
  setHighlightedIndex,
  className,
}: LiquidityChartComponentProps) {
  const totalAmount = items.reduce((acc, item) => acc.plus(item.amount), NormalizedNumber.ZERO)
  if (totalAmount.eq(NormalizedNumber.ZERO)) {
    return null
  }

  const displayedItems = items.filter((item) => item.amount.div(totalAmount).gt(0.005))

  const data = displayedItems.map((item, index) => ({
    value: item.amount.toNumber(),
    color: getPalletteColor(index),
  }))
  const unfilteredData = items.map((item, index) => ({
    value: item.amount.toNumber(),
    color: getPalletteColor(index),
  }))

  const highlightedItem = highlightedIndex === undefined ? undefined : displayedItems[highlightedIndex]

  const radius = 146
  const strokeWidth = 26
  const arcs = getArcs({ data, cx: 200, cy: 200, radius })
  const separators = getSeparators({
    data,
    unfilteredData,
    cx: 200,
    cy: 200,
    radius,
    mainStrokeWidth: strokeWidth,
  })

  return (
    <div
      className={cn('relative isolate h-fit w-full', className)}
      style={{
        container: 'sidebar / inline-size',
      }}
    >
      <svg preserveAspectRatio="xMidYMid meet" viewBox="0 0 400 400">
        {arcs.map((point, index) => {
          if (point.angle <= 0) {
            return null
          }

          return (
            <path
              d={`M${point.x1} ${point.y1} A${radius} ${radius} 0 ${point.angle > Math.PI ? '1' : '0'} 1 ${point.x2} ${point.y2}`}
              fill="none"
              stroke={highlightedIndex === index ? darkenColor(point.color) : point.color}
              strokeWidth={strokeWidth}
              style={{
                cursor: 'pointer',
                transition: 'stroke 0.2s ease-in-out',
              }}
              key={`${point.x1}${point.y1}${point.x2}${point.y2}`}
              onMouseEnter={() => {
                setHighlightedIndex(index)
              }}
              onMouseLeave={() => {
                setHighlightedIndex(undefined)
              }}
            />
          )
        })}
        {separators.length > 1 &&
          separators.map((separator) => (
            <path
              d={`M${separator.x1} ${separator.y1} L${separator.x2} ${separator.y2}`}
              fill="none"
              stroke="white"
              strokeWidth={2}
              key={`${separator.x1}${separator.y1}${separator.x2}${separator.y2}`}
            />
          ))}
      </svg>
      <div className="-z-10 absolute inset-0">
        <div className="relative flex h-full w-full flex-col items-center justify-center text-primary">
          <div
            className={cn(
              'absolute inset-0 flex flex-col items-center justify-center transition-opacity delay-100 duration-200 ease-out',
              highlightedItem === undefined ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <div className="font-roobert text-[3cqw] text-secondary">Total Liquidity</div>
            <div className="font-roobert text-[7cqw]">{formatPieChartValue(totalAmount)}</div>
            <div className="font-roobert text-[3cqw] text-secondary">USD</div>
          </div>

          {displayedItems.map((item, index) => (
            <div
              key={`highlighted-${index}`}
              className={cn(
                'absolute inset-0 flex flex-col items-center justify-center transition-opacity delay-100 duration-200 ease-out',
                highlightedIndex === index ? 'opacity-100' : 'pointer-events-none opacity-0',
              )}
            >
              <div className="flex items-center gap-[1cqw]">
                <div
                  className="size-[1.5cqw] rounded-full"
                  style={{ backgroundColor: darkenColor(getPalletteColor(index)) }}
                />
                <div className="font-roobert text-[3cqw] text-secondary">{mapProtocolToLabel(item.protocol)}</div>
              </div>
              <div className="font-roobert text-[7cqw]">{formatPieChartValue(item.amount)}</div>
              <div className="font-roobert text-[3cqw] text-secondary">USD</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
